import React from "react";

import { useTranslation } from "react-i18next";

import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";

import type { JSX } from "react";

import type { EmbedProfile } from "@/src/api/ProjectAPI.types";

const VISIBLE_DOMAIN_COUNT = 3;

interface EmbedProfileRowProps {
  profile: EmbedProfile;
  canWrite: boolean;
  onEdit: (profile: EmbedProfile) => void;
  onDelete: (profile: EmbedProfile) => void;
}

export default function EmbedProfileRow({
  profile,
  canWrite,
  onEdit,
  onDelete,
}: EmbedProfileRowProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const hiddenDomainCount = profile.domains.length - VISIBLE_DOMAIN_COUNT;

  return (
    <li className="border-border flex items-center gap-3 border-b px-6.25 py-4 last:border-b-0">
      <span className="font-medium">{profile.label}</span>
      {profile.default ? (
        <Badge className="text-primary border-primary/30" variant="outline">
          {t("embedProfiles.badgeDefault")}
        </Badge>
      ) : null}
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        {profile.domains.slice(0, VISIBLE_DOMAIN_COUNT).map((domain) => (
          <Badge key={domain} className="font-mono" variant="secondary">
            {domain}
          </Badge>
        ))}
        {hiddenDomainCount > 0 ? (
          <span className="text-muted-foreground text-xs">
            {t("embedProfiles.moreDomains", { count: hiddenDomainCount })}
          </span>
        ) : null}
      </div>
      {canWrite ? (
        <div className="flex gap-2">
          <Button
            onClick={() => onEdit(profile)}
            size="sm"
            variant="outline"
            aria-label={t("embedProfiles.actions.editNamed", {
              label: profile.label,
            })}
          >
            {t("embedProfiles.actions.edit")}
          </Button>
          <Button
            onClick={() => onDelete(profile)}
            size="sm"
            variant="outline"
            aria-label={t("embedProfiles.actions.deleteNamed", {
              label: profile.label,
            })}
          >
            {t("embedProfiles.actions.delete")}
          </Button>
        </div>
      ) : null}
    </li>
  );
}
